import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Crown, Plus, Save, Trash2 } from 'lucide-react';
import { api } from '../services/api';

type TierBenefit = {
  id: number;
  name: string;
  benefit_type: string;
  benefit_value?: Record<string, any> | null;
  description?: string | null;
  is_active: boolean;
};

type LoyaltyTier = {
  id: number;
  name: string;
  rank: number;
  min_points_earned: number;
  min_purchase_count: number;
  min_total_spent: string;
  points_multiplier: string;
  is_active: boolean;
  benefits: TierBenefit[];
};

type LoyaltySettings = {
  evaluation_period_days: number;
  allow_downgrade: boolean;
  downgrade_grace_days: number;
};

const Container = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  padding: 24px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e2e8f0;
  color: #0f172a;
  font-weight: 600;
  cursor: pointer;
`;

const Title = styled.h1`
  font-size: 1.7rem;
  color: #0f172a;
  margin: 0;
`;

const Subtitle = styled.p`
  margin: 0 0 1.5rem;
  color: #475569;
`;

const Grid = styled.div`
  display: grid;
  gap: 16px;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 16px;
`;

const CardTitle = styled.h2`
  margin: 0 0 12px;
  font-size: 1.05rem;
  color: #0f172a;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(120px, 1fr));
  gap: 10px;
  margin-bottom: 10px;

  @media (max-width: 980px) {
    grid-template-columns: repeat(2, minmax(140px, 1fr));
  }
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid #dbe2ea;
  border-radius: 8px;
  padding: 9px 10px;
`;

const Select = styled.select`
  width: 100%;
  border: 1px solid #dbe2ea;
  border-radius: 8px;
  padding: 9px 10px;
  background: #fff;
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.84rem;
  color: #334155;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  border-radius: 8px;
  padding: 9px 12px;
  background: #0f172a;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
`;

const GhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #dbe2ea;
  border-radius: 8px;
  padding: 9px 12px;
  background: #fff;
  color: #0f172a;
  font-weight: 600;
  cursor: pointer;
`;

const DangerButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 9px 12px;
  background: #fef2f2;
  color: #b91c1c;
  font-weight: 600;
  cursor: pointer;
`;

const TierHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
`;

const TierName = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  color: #0f172a;
`;

const Pill = styled.span`
  font-size: 0.75rem;
  border-radius: 999px;
  padding: 2px 8px;
  border: 1px solid #bae6fd;
  background: #f0f9ff;
  color: #0369a1;
`;

const BenefitItem = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px;
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const SmallText = styled.span`
  color: #64748b;
  font-size: 0.82rem;
`;

export const LoyaltyTiersPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<LoyaltySettings>({
    evaluation_period_days: 365,
    allow_downgrade: true,
    downgrade_grace_days: 30,
  });
  const [tiers, setTiers] = useState<LoyaltyTier[]>([]);
  const [newTier, setNewTier] = useState({
    name: '',
    rank: '1',
    min_points_earned: '0',
    min_purchase_count: '0',
    min_total_spent: '0',
    points_multiplier: '1',
  });
  const [benefitDraft, setBenefitDraft] = useState<Record<number, { name: string; benefit_type: string; description: string }>>({});

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/loyalty/tiers');
      const data = response.data.data || {};
      if (data.settings) {
        setSettings({
          evaluation_period_days: Number(data.settings.evaluation_period_days ?? 365),
          allow_downgrade: Boolean(data.settings.allow_downgrade),
          downgrade_grace_days: Number(data.settings.downgrade_grace_days ?? 30),
        });
      }
      setTiers(data.tiers || []);
    } catch (error: any) {
      window.alert(error?.response?.data?.message || 'Erro ao carregar níveis de fidelidade.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const saveSettings = async () => {
    try {
      await api.put('/loyalty/settings', settings);
      window.alert('Configurações salvas.');
    } catch (error: any) {
      window.alert(error?.response?.data?.message || 'Erro ao salvar configurações.');
    }
  };

  const createTier = async () => {
    if (!newTier.name.trim()) {
      window.alert('Informe o nome do nível.');
      return;
    }
    try {
      await api.post('/loyalty/tiers', {
        name: newTier.name.trim(),
        rank: Number(newTier.rank || 1),
        min_points_earned: Number(newTier.min_points_earned || 0),
        min_purchase_count: Number(newTier.min_purchase_count || 0),
        min_total_spent: Number(newTier.min_total_spent || 0),
        points_multiplier: Number(newTier.points_multiplier || 1),
        is_active: true,
      });
      setNewTier({
        name: '',
        rank: '1',
        min_points_earned: '0',
        min_purchase_count: '0',
        min_total_spent: '0',
        points_multiplier: '1',
      });
      loadData();
    } catch (error: any) {
      window.alert(error?.response?.data?.message || 'Erro ao criar nível.');
    }
  };

  const updateTier = async (tier: LoyaltyTier) => {
    try {
      await api.put(`/loyalty/tiers/${tier.id}`, {
        name: tier.name,
        rank: tier.rank,
        min_points_earned: tier.min_points_earned,
        min_purchase_count: tier.min_purchase_count,
        min_total_spent: Number(tier.min_total_spent || 0),
        points_multiplier: Number(tier.points_multiplier || 1),
        is_active: tier.is_active,
      });
      window.alert('Nível atualizado.');
      loadData();
    } catch (error: any) {
      window.alert(error?.response?.data?.message || 'Erro ao atualizar nível.');
    }
  };

  const deleteTier = async (tierId: number) => {
    if (!window.confirm('Deseja remover este nível?')) return;
    try {
      await api.delete(`/loyalty/tiers/${tierId}`);
      loadData();
    } catch (error: any) {
      window.alert(error?.response?.data?.message || 'Erro ao remover nível.');
    }
  };

  const addBenefit = async (tierId: number) => {
    const draft = benefitDraft[tierId] || { name: '', benefit_type: 'general', description: '' };
    if (!draft.name.trim()) {
      window.alert('Informe o nome do benefício.');
      return;
    }
    try {
      await api.post(`/loyalty/tiers/${tierId}/benefits`, {
        name: draft.name.trim(),
        benefit_type: draft.benefit_type || 'general',
        description: draft.description || null,
        is_active: true,
      });
      setBenefitDraft(prev => ({
        ...prev,
        [tierId]: { name: '', benefit_type: 'general', description: '' },
      }));
      loadData();
    } catch (error: any) {
      window.alert(error?.response?.data?.message || 'Erro ao adicionar benefício.');
    }
  };

  const removeBenefit = async (benefitId: number) => {
    if (!window.confirm('Deseja remover este benefício?')) return;
    try {
      await api.delete(`/loyalty/benefits/${benefitId}`);
      loadData();
    } catch (error: any) {
      window.alert(error?.response?.data?.message || 'Erro ao remover benefício.');
    }
  };

  if (loading) {
    return <Container>Carregando programa de níveis...</Container>;
  }

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/dashboard')}>
          <ArrowLeft size={16} />
          Voltar
        </BackButton>
        <Title>Programa de Níveis</Title>
      </Header>
      <Subtitle>Configure critérios de progressão e benefícios por nível para aumentar retenção e recorrência.</Subtitle>

      <Grid>
        <Card>
          <CardTitle>Configurações do Programa</CardTitle>
          <Row>
            <Label>
              Janela de avaliação (dias)
              <Input
                type="number"
                min={30}
                value={settings.evaluation_period_days}
                onChange={e => setSettings(prev => ({ ...prev, evaluation_period_days: Number(e.target.value || 365) }))}
              />
            </Label>
            <Label>
              Permitir downgrade
              <Select
                value={settings.allow_downgrade ? 'yes' : 'no'}
                onChange={e => setSettings(prev => ({ ...prev, allow_downgrade: e.target.value === 'yes' }))}
              >
                <option value="yes">Sim</option>
                <option value="no">Não</option>
              </Select>
            </Label>
            <Label>
              Grace period downgrade (dias)
              <Input
                type="number"
                min={0}
                value={settings.downgrade_grace_days}
                onChange={e => setSettings(prev => ({ ...prev, downgrade_grace_days: Number(e.target.value || 0) }))}
              />
            </Label>
          </Row>
          <ActionRow>
            <PrimaryButton onClick={saveSettings}>
              <Save size={15} />
              Salvar configurações
            </PrimaryButton>
          </ActionRow>
        </Card>

        <Card>
          <CardTitle>Novo Nível</CardTitle>
          <Row>
            <Label>
              Nome
              <Input value={newTier.name} onChange={e => setNewTier(prev => ({ ...prev, name: e.target.value }))} />
            </Label>
            <Label>
              Rank
              <Input type="number" min={1} value={newTier.rank} onChange={e => setNewTier(prev => ({ ...prev, rank: e.target.value }))} />
            </Label>
            <Label>
              Pontos mínimos (janela)
              <Input type="number" min={0} value={newTier.min_points_earned} onChange={e => setNewTier(prev => ({ ...prev, min_points_earned: e.target.value }))} />
            </Label>
            <Label>
              Compras mínimas (janela)
              <Input type="number" min={0} value={newTier.min_purchase_count} onChange={e => setNewTier(prev => ({ ...prev, min_purchase_count: e.target.value }))} />
            </Label>
            <Label>
              Gasto mínimo (janela)
              <Input type="number" min={0} step="0.01" value={newTier.min_total_spent} onChange={e => setNewTier(prev => ({ ...prev, min_total_spent: e.target.value }))} />
            </Label>
            <Label>
              Multiplicador de pontos
              <Input type="number" min={1} step="0.01" value={newTier.points_multiplier} onChange={e => setNewTier(prev => ({ ...prev, points_multiplier: e.target.value }))} />
            </Label>
          </Row>
          <ActionRow>
            <PrimaryButton onClick={createTier}>
              <Plus size={15} />
              Criar nível
            </PrimaryButton>
          </ActionRow>
        </Card>

        {tiers.map(tier => (
          <Card key={tier.id}>
            <TierHeader>
              <TierName>
                <Crown size={16} />
                {tier.name}
                <Pill>Rank {tier.rank}</Pill>
              </TierName>
              <ActionRow>
                <GhostButton onClick={() => updateTier(tier)}>
                  <Save size={15} />
                  Salvar
                </GhostButton>
                <DangerButton onClick={() => deleteTier(tier.id)}>
                  <Trash2 size={15} />
                  Excluir
                </DangerButton>
              </ActionRow>
            </TierHeader>

            <Row>
              <Label>
                Nome
                <Input value={tier.name} onChange={e => setTiers(prev => prev.map(item => item.id === tier.id ? { ...item, name: e.target.value } : item))} />
              </Label>
              <Label>
                Rank
                <Input type="number" min={1} value={tier.rank} onChange={e => setTiers(prev => prev.map(item => item.id === tier.id ? { ...item, rank: Number(e.target.value || 1) } : item))} />
              </Label>
              <Label>
                Pontos mínimos
                <Input type="number" min={0} value={tier.min_points_earned} onChange={e => setTiers(prev => prev.map(item => item.id === tier.id ? { ...item, min_points_earned: Number(e.target.value || 0) } : item))} />
              </Label>
              <Label>
                Compras mínimas
                <Input type="number" min={0} value={tier.min_purchase_count} onChange={e => setTiers(prev => prev.map(item => item.id === tier.id ? { ...item, min_purchase_count: Number(e.target.value || 0) } : item))} />
              </Label>
              <Label>
                Gasto mínimo
                <Input type="number" min={0} step="0.01" value={tier.min_total_spent} onChange={e => setTiers(prev => prev.map(item => item.id === tier.id ? { ...item, min_total_spent: e.target.value } : item))} />
              </Label>
              <Label>
                Multiplicador
                <Input type="number" min={1} step="0.01" value={tier.points_multiplier} onChange={e => setTiers(prev => prev.map(item => item.id === tier.id ? { ...item, points_multiplier: e.target.value } : item))} />
              </Label>
              <Label>
                Ativo
                <Select value={tier.is_active ? 'yes' : 'no'} onChange={e => setTiers(prev => prev.map(item => item.id === tier.id ? { ...item, is_active: e.target.value === 'yes' } : item))}>
                  <option value="yes">Sim</option>
                  <option value="no">Não</option>
                </Select>
              </Label>
            </Row>

            <CardTitle>Benefícios</CardTitle>
            {tier.benefits.length === 0 && <SmallText>Nenhum benefício cadastrado.</SmallText>}
            {tier.benefits.map(benefit => (
              <BenefitItem key={benefit.id}>
                <div>
                  <strong>{benefit.name}</strong>
                  <SmallText> {benefit.benefit_type}</SmallText>
                  {benefit.description ? <div><SmallText>{benefit.description}</SmallText></div> : null}
                </div>
                <DangerButton onClick={() => removeBenefit(benefit.id)}>
                  <Trash2 size={14} />
                  Remover
                </DangerButton>
              </BenefitItem>
            ))}

            <Row>
              <Label>
                Nome do benefício
                <Input
                  value={benefitDraft[tier.id]?.name || ''}
                  onChange={e => setBenefitDraft(prev => ({ ...prev, [tier.id]: { ...(prev[tier.id] || { benefit_type: 'general', description: '' }), name: e.target.value } }))}
                />
              </Label>
              <Label>
                Tipo
                <Input
                  placeholder="ex: points_multiplier, priority_support"
                  value={benefitDraft[tier.id]?.benefit_type || 'general'}
                  onChange={e => setBenefitDraft(prev => ({ ...prev, [tier.id]: { ...(prev[tier.id] || { name: '', description: '' }), benefit_type: e.target.value } }))}
                />
              </Label>
              <Label>
                Descrição
                <Input
                  value={benefitDraft[tier.id]?.description || ''}
                  onChange={e => setBenefitDraft(prev => ({ ...prev, [tier.id]: { ...(prev[tier.id] || { name: '', benefit_type: 'general' }), description: e.target.value } }))}
                />
              </Label>
            </Row>
            <ActionRow>
              <PrimaryButton onClick={() => addBenefit(tier.id)}>
                <Plus size={14} />
                Adicionar benefício
              </PrimaryButton>
            </ActionRow>
          </Card>
        ))}
      </Grid>
    </Container>
  );
};

